/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { ErrorResponseModel, NotificationModel } from '../../../../../core/models';
import { LogService, MenuService, ModalService, NotifyService, UtilsService } from '../../../../../core/services';
import { TemplateModel } from '../../models';
import { WizardService, WizardTemplateService } from '../../services/components';
import { WizardStepModel } from '../../models/wizardStep.model';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-wizard',
    styleUrls: ['./wizard.component.scss'],
    templateUrl: './wizard.component.html',
    encapsulation: ViewEncapsulation.None
})

export class WizardComponent extends BaseComponent implements OnInit, OnDestroy {
    /**
     * Set available templates.
     * @param availableTemplates
     */
    @Input() set availableTemplates(availableTemplates: TemplateModel[]) {
        this._availableTemplates = availableTemplates;
        this.setupWizard();
    }

    /**
     * Show advanced mode - The ability to not show advance mode.
     */
    @Input() public wizardModeState: 'hidden' | true | false = false;

    /**
     * Show save button for exporting templates.
     */
    @Input() public showSaveButton: boolean = false;

    /**
     * Show back button.
     */
    @Input() public showBackButton: boolean = true;

    /**
     * Show extra service button at the end of each parent step.
     */
    @Input() public showExtraServicesButton: boolean = false;

    /**
     * Show the progress bar.
     */
    @Input() public showProgressBar: boolean = true;

    /**
     * Show wizard steps vertically (navigation left/ content right).
     */
    @Input() public vertical: boolean = false;

    /**
     * Main title for the wizard.
     */
    @Input() public wizardTitle: string;

    /**
     * Text for cancel wizard button.
     */
    @Input() public cancelButtonText: string;

    /**
     * Hold submission state for the wizard.
     */
    @Input() public submissionActive: boolean = false;

    /**
     * Event emitter for our completion state.
     */
    @Output() public wizardComplete: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Event emitter for saving state.
     */
    @Output() public saveTemplate: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Event emitter for cancelling wizard process.
     */
    @Output() public exitWizard: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        wizardMode: []
    };

    /**
     * State of current form section validity.
     */
    public buttonIsDisabled: boolean = true;

    /**
     * Error notification.
     */
    public errorNotification: NotificationModel;

    /**
     * Set true if incoming template and there is more than one template.
     */
    public showTemplateSelector: boolean = false;

    /**
     * Stored output from template selector component.
     */
    public selectedTemplate: TemplateModel;

    /**
     * Selected wizard mode to set selected key on wizard mode dropdown.
     */
    public wizardSelectedMode: string;

    /**
     * Reference to the steps in the wizard service.
     */
    public wizardStepsReference: WizardStepModel[] = [];

    /**
     * Flag to store if the current step is the last step in a section.
     */
    public isLastStepSection: boolean = false;

    /**
     * Store the current step service and index.
     */
    public currentStep: any;

    /**
     * List of IDs to enable the view to get the visible step index.
     * @private
     */
    private visibleStepsLookUp: any[] = [];

    /**
     * Store the available templates.
     * @private
     */
    private _availableTemplates: TemplateModel[] = [];

    public constructor(
        public wizardService: WizardService,
        private cdRef: ChangeDetectorRef,
        private logService: LogService,
        private menuService: MenuService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private utilsService: UtilsService,
        private wizardTemplateService: WizardTemplateService,
    ) {
        super();

        this.setupSubscribers();
        this.setupReLoginModalListener();
        this.wizardService.disableNavigation = false;
    }

    /**
     * Available templates getter.
     */
    get availableTemplates() {
        return this._availableTemplates;
    }

    public ngOnInit(): void {
        this.menuService.menuIsOpen.next(false);
        this.cdRef.detectChanges();
    }

    public ngOnDestroy(): void {
        this.menuService.menuIsOpen.next(true);
        this.notifyService.removeNotificationById('wizardError');
        this.wizardService.resetWizardService();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Get visible step index.
     * @param step
     */
    public getVisibleStepIndex(step: WizardStepModel): number {
        const visibleIndex: number = this.visibleStepsLookUp.findIndex(item => item === step.id);
        if (visibleIndex !== -1) {
            return visibleIndex + 1;
        }
    }

    /**
     * Helper function to show/hide wizard mode dropdown.
     */
    public showWizardModeDropdown(): boolean {
        return this.wizardModeState
            && !!(this.dropdown.wizardMode && this.dropdown.wizardMode.length)
            && !this.showTemplateSelector
            && !this.wizardService.selectedTemplate.wizardHideModes;
    }

    /**
     * Show add another service button.
     */
    public showAddAnotherButton(): boolean {
        return this.showExtraServicesButton && this.isLastStepSection && !this.wizardService.disableNavigation;
    }

    /**
     * Add another service based on the current template and section.
     */
    public addService(): void {
        this.wizardService.addService(this.currentStep);
    }

    /**
     * Set selected wizard mode in the service.
     * @param event
     */
    public setWizardMode(event: any): void {
        this.wizardService.wizardModeEmitter.next(event);
    }

    /**
     * Emit the cancel submission.
     */
    public cancelSubmission(): void {
        this.exitWizard.emit(true);
    }

    /**
     * Go to the previous step in the wizard process
     */
    public previous(): void {
        this.wizardService.previousStep();
    }

    /**
     * Function to poke the save emitter.
     */
    public saveTemplateToFile(): void {
        this.saveTemplate.emit(true);
    }

    /**
     * Select template function
     */
    public selectTemplate(): void {
        this.wizardTitle = this.selectedTemplate.title;
        this.wizardService.setupWizardModesDropdown(this.selectedTemplate);
        this.wizardService.selectedTemplate = this.selectedTemplate;
        this.showExtraServicesButton = this.selectedTemplate?.hideMultipleServices
            ? this.selectedTemplate?.hideMultipleServices
            : true;
        const controllerObj: any = Object.keys(this.wizardService.controllerState);
        if (controllerObj.length === 0) {
            this.wizardService.createServiceStates();
        } else {
            this.wizardService.nextStep();
        }

        this.showTemplateSelector = false;
    }

    /**
     * Go to the next step in the wizard process
     */
    public next(): void {
        this.showTemplateSelector = false;
        this.buttonIsDisabled = true;
        this.wizardService.nextStep();
        this.cdRef.detectChanges();
    }

    /**
     * Show finish button.
     */
    public showFinishButton(): boolean {
        return this.wizardService.isLastStep || this.submissionActive;
    }

    /**
     * Show force finish button.
     */
    public showForceFinishButton(): boolean {
        const hadErrors: boolean = !!this.utilsService.getObjectValue(this.wizardService.controllerState, 'errorState');

        return !this.wizardService.isLastStep && hadErrors;
    }

    /**
     * function to poke to emit the complete emitter
     */
    public finish(): void {
        if (this.buttonIsDisabled) {
            this.logService.debug('WizardComponent:finish', 'Button is disabled');
        } else {
            this.wizardComplete.emit(true);
        }
    }

    /**
     * Close error notification emitter
     * @param notificationId
     */
    public closeErrorNotification(notificationId: string): void {
        this.wizardService.errorNotificationClosed.next(true);
        this.notifyService.removeNotificationById(notificationId);
    }

    /**
     * Setup wizard subscribers
     * @private
     */
    private setupSubscribers(): void {
        this.notifyService.updateNotificationsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (notifications: NotificationModel[]): void => {
                    const wizardError: NotificationModel = notifications.find(
                        (item: NotificationModel): boolean => item.id === 'wizardError'
                    );
                    if (wizardError) {
                        wizardError['class'] = this.getLevelClassName(wizardError.level);
                        this.errorNotification = wizardError;
                        this.wizardService.submissionActive = false;
                    } else {
                        this.errorNotification = null;
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.warn('NotificationWrapperComponent: ', error.message);
                }
            });

        this.wizardService.isSectionValidEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (isValid: boolean): void => {
                    this.buttonIsDisabled = !isValid;
                    this.cdRef.detectChanges();
                }
            });

        this.wizardService.parentStepEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (step: any): void => {
                    this.currentStep = step;
                    if (step.service === 'templateSelection') {
                        this.showTemplateSelector = true;
                    }
                }
            });

        this.wizardService.wizardStepObjectEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (steps): void => {
                    this.wizardStepsReference = this.createStepIds(steps);
                    this.createVisibleStepLookup(steps);
                }
            });

        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (wizardMode: any): void => {
                    this.wizardSelectedMode = wizardMode;
                    this.dropdown.wizardMode = this.wizardService.wizardModeDropdown;
                }
            });

        this.wizardService.wizardModeDropdownEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.dropdown.wizardMode = this.wizardService.wizardModeDropdown;
                }
            });

        this.wizardService.isLastSectionInService
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    if (!this.wizardService.excludeFromAddAnother.includes(this.currentStep.service) &&
                        !this.selectedTemplate?.hideMultipleServices
                    ) {
                        this.showExtraServicesButton = state;
                        this.isLastStepSection = state;
                    } else {
                        this.showExtraServicesButton = false;
                    }
                }
            });

        this.wizardTemplateService.templateChangeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (template: TemplateModel): void => {
                    this.selectedTemplate = template;
                }
            });
    }

    /**
     * Gets notifications class name from the level.
     * @param level
     * @private
     */
    private getLevelClassName(level: number): string {
        return ({
            5: 'debug',
            4: 'information',
            3: 'success',
            2: 'warning',
            1: 'error'
        })[level];
    }

    /**
     * Create step Ids.
     * @param steps
     * @param prefix
     * @private
     */
    private createStepIds(steps: WizardStepModel[], prefix?: string): WizardStepModel[] {
        for (const step of steps) {
            const stringToConvert: string = prefix ? prefix + '-' + step.title : step.title;
            step['htmlId'] = this.utilsService.convertStringToId(stringToConvert);
            if (step.subSteps?.length) {
                step.subSteps = this.createStepIds(step.subSteps, step['htmlId']);
            }
        }

        return steps;
    }

    /**
     * Create list of visible step Ids for lookup in view.
     * @param steps
     * @private
     */
    private createVisibleStepLookup(steps: WizardStepModel[]): void {
        const visibleSteps: WizardStepModel[] = steps.filter((step: WizardStepModel) => step.isVisible);
        this.visibleStepsLookUp = visibleSteps.map((item: WizardStepModel) => item.id);
    }

    /**
     * Listen for the re-login emitter and cancel the wizard.
     * @private
     */
    private setupReLoginModalListener(): void {
        this.modalService.openParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(modal => {
                if (modal.modalId === 'relogin') {
                    this.cancelSubmission();
                }
            });
    }

    /**
     * Setup wizard.
     * @private
     */
    private setupWizard(): void {
        this.wizardService.setupWizardSubscribers();
        // if no selected template and template length > 1 show template selector
        if (this.availableTemplates?.length > 1) {
            this.showTemplateSelector = true;
            this.wizardService.createTemplateSelectionStep();
        } else if (this.availableTemplates?.length === 1) {
            // Pass a clone of the template into the services so that the original template can be referenced later
            const clonedTemplate = this.utilsService.duplicateObject(this.availableTemplates[0]);
            this.wizardService.setupWizardModesDropdown(clonedTemplate);
            this.wizardService.selectedTemplate = clonedTemplate;
            this.wizardService.createWizardStepStateFromTemplate(clonedTemplate);
        } else {
            this.logService.error('WizardComponent: setupWizard', 'No templates available');
        }

        this.wizardService.setFirstStepActive();
    }
}
